/**
 * App-health analytics wiring (RFC-0974 follow-up, §5.1/§5.2/§5.6).
 *
 * Mounted ONCE from `App.tsx`. Owns three global signals:
 *  - `app_session_started` — once per session after authenticated restoration.
 *  - `app_error_uncaught`  — window `error` / `unhandledrejection` (ADDITIVE
 *    `addEventListener`, never `window.onerror=`, so other listeners are
 *    untouched). Raw error class name + bundle site + area — never
 *    messages/stacks/rejection values (§4).
 *  - `app_inp_reported`    — web-vitals INP, beaconed anonymously on page-hide.
 *
 * The DOM listeners + web-vitals subscription are installed via a MODULE-LEVEL
 * idempotent singleton (survives StrictMode double-mount; HMR-disposed) so they
 * are never duplicated. Everything is gated by the emitter's consent/env checks;
 * error/session emission additionally gates on live authentication (the event
 * tier is AUTHENTICATED — a pre-auth landing error cannot get a JWT, so it is
 * intentionally NOT captured; this is a documented coverage limitation).
 */

import { useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { registerGrantHandler } from './consentGate'
import { getSessionId } from './sessionId'
import { getPageId } from './pageId'
import { readErrorName } from './errorClass'
import { siteFromErrorEvent, siteFromRejectionReason } from './errorSite'
import { initEventLifecycle } from './eventsClient'
import {
  trackAppErrorUncaught,
  trackAppInpReported,
  trackAppSessionStarted,
  type AppArea,
  type InteractionArea,
} from './events'

// ─── live auth ref (read by the global error listeners) ─────────────────────
let authed = false

// ─── area / project derivation from the raw pathname ────────────────────────
/** Coarse app area for `app_error_uncaught` (§5.2), from the current path. */
export function deriveArea(pathname: string): AppArea {
  if (/^\/project\/[^/]+\/workspace/.test(pathname)) return 'workspace'
  if (/^\/(dashboard|settings|arena|admin)/.test(pathname)) return 'dashboard'
  if (
    pathname === '/' ||
    /^\/(login|landing|landing-v2|for-agents|reset-password|account|published|templates)/.test(
      pathname,
    )
  ) {
    return 'landing'
  }
  return 'other'
}

/** Project id when inside a project route, else undefined (Opt field, §5.2). */
export function parseProjectId(pathname: string): string | undefined {
  const m = /^\/project\/([^/]+)/.exec(pathname)
  return m ? m[1] : undefined
}

/**
 * Coarse interaction area for INP (§5.6), from the responsible element. Selectors
 * are real, stable app markers (verified against the DOM):
 *  - timeline: the fixed TimelineEditorModal overlay (`timeline-modal-backdrop`),
 *    checked FIRST since it stacks over the canvas.
 *  - chat: the Director chatbox `<aside>` (`data-canvas-interaction-ignore`).
 *  - canvas: the ReactFlow root (`.react-flow`).
 */
export function deriveInteractionArea(el: Element | null | undefined): InteractionArea {
  if (!el) return 'other'
  try {
    if (el.closest('[data-testid="timeline-modal-backdrop"]')) return 'timeline'
    if (el.closest('[data-canvas-interaction-ignore]')) return 'chat'
    if (el.closest('.react-flow')) return 'canvas'
  } catch {
    /* fall through */
  }
  return 'other'
}

// ─── module-level singleton install ─────────────────────────────────────────
let installed = false
let active = false
let teardown: (() => void) | null = null
const inpSeen = new Set<string>()

function onWindowError(event: ErrorEvent): void {
  if (!active || !authed) return
  try {
    trackAppErrorUncaught({
      error_kind: 'error',
      error_name: readErrorName(event.error),
      error_site: siteFromErrorEvent(event),
      area: deriveArea(window.location.pathname),
      project_id: parseProjectId(window.location.pathname),
    })
  } catch {
    /* never let telemetry break the product */
  }
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  if (!active || !authed) return
  try {
    trackAppErrorUncaught({
      error_kind: 'unhandledrejection',
      error_name: readErrorName(event.reason),
      error_site: siteFromRejectionReason(event.reason),
      area: deriveArea(window.location.pathname),
      project_id: parseProjectId(window.location.pathname),
    })
  } catch {
    /* never let telemetry break the product */
  }
}

function installAppHealthListeners(): void {
  if (installed) return
  if (typeof window === 'undefined') return
  installed = true
  active = true
  window.addEventListener('error', onWindowError)
  window.addEventListener('unhandledrejection', onUnhandledRejection)

  // If consent is granted AFTER auth is already established, retry the once-per-
  // session denominator (it was suppressed while consent was pending).
  registerGrantHandler(() => {
    if (authed) maybeEmitSessionStarted()
  })

  // INP: web-vitals default reporting fires the callback with the FINAL INP when
  // the document is backgrounded (and again after a bfcache restore→re-hide with
  // a NEW metric.id). Beacon each distinct metric.id once. Anonymous — no auth
  // gate (INP is not joined to users, §4); consent/env gating happens in the
  // beacon emitter.
  void import('web-vitals/attribution')
    .then(({ onINP }) => {
      if (!active) return
      onINP((metric) => {
        if (!active) return
        try {
          if (inpSeen.has(metric.id)) return
          // Bound the set (LRU-ish): evict the OLDEST id before adding — never
          // clear() (that would drop the id we just added and let it re-emit).
          if (inpSeen.size >= 128) {
            const oldest = inpSeen.values().next().value
            if (oldest !== undefined) inpSeen.delete(oldest)
          }
          inpSeen.add(metric.id)
          // web-vitals types interactionTargetElement as Node; narrow to Element
          // via instanceof (no `as` cast — forbidden by the repo TS gate).
          const target = metric.attribution?.interactionTargetElement
          const el = target instanceof Element ? target : null
          trackAppInpReported({
            inp_ms: Math.round(metric.value),
            page_id: getPageId(),
            interaction_area: deriveInteractionArea(el),
          })
        } catch {
          /* best-effort */
        }
      })
    })
    .catch(() => {
      /* web-vitals unavailable — INP simply not reported */
    })

  teardown = () => {
    window.removeEventListener('error', onWindowError)
    window.removeEventListener('unhandledrejection', onUnhandledRejection)
  }
}

/** Test/HMR helper — remove listeners and disable the (non-unsubscribable) INP cb. */
export function _resetAppHealthForTest(): void {
  active = false
  if (teardown) teardown()
  teardown = null
  installed = false
  inpSeen.clear()
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => _resetAppHealthForTest())
}

// ─── session-started (once per session_id, persisted across reloads) ────────
const SESSION_STARTED_KEY = 'dw_evt_session_started'
let memoryStartedSession: string | null = null

function readStartedSession(): string | null {
  try {
    return sessionStorage.getItem(SESSION_STARTED_KEY) ?? memoryStartedSession
  } catch {
    return memoryStartedSession
  }
}

function writeStartedSession(sessionId: string): void {
  memoryStartedSession = sessionId
  try {
    sessionStorage.setItem(SESSION_STARTED_KEY, sessionId)
  } catch {
    /* storage blocked — in-memory mirror is the fallback */
  }
}

function maybeEmitSessionStarted(): void {
  try {
    const sid = getSessionId()
    if (readStartedSession() === sid) return
    // Only record the once-per-session marker if the event was actually accepted
    // for emission (env + consent). If it was dropped, leave the marker unset so
    // a later call (e.g. after consent is granted) can still emit it.
    if (trackAppSessionStarted()) writeStartedSession(sid)
  } catch {
    /* best-effort */
  }
}

// Install at MODULE IMPORT (App.tsx import time), before React mounts / the first
// passive effect — so the window error/unhandledrejection listeners and the INP
// (web-vitals) subscription are live from app boot rather than after mount. This
// closes the mount→effect gap and lets web-vitals observe interactions from the
// start of the document. Idempotent + SSR/test-guarded. (Pre-auth errors still
// don't EMIT — that's the AUTHENTICATED-tier design limitation, documented.)
initEventLifecycle()
installAppHealthListeners()

/**
 * Fire `app_session_started` once the user is authenticated, and keep the global
 * listeners installed (defensive re-init after HMR / a test reset). The listeners
 * themselves are installed at module import above.
 */
export function useAppHealthAnalytics(): void {
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    // Defensive: re-install if a HMR dispose / test reset tore listeners down.
    initEventLifecycle()
    installAppHealthListeners()
    authed = isAuthenticated
    if (isAuthenticated) maybeEmitSessionStarted()
  }, [isAuthenticated])
}
